import { funnel, doNothing } from 'remeda'
import React, { RefObject, createContext, useContext, useEffect, useRef, useState } from 'react'
import { ReactNode } from 'react'
import { Key } from 'react-aria'
import { ListState } from 'react-stately'
import { useSelectItemData } from '../base-select/SelectItemContext'

export const VirtualItemWrapperFromContext = (props: {
  children: ReactNode
  itemKey: Key
  selectionManager: ListState<unknown>['selectionManager']
}) => {
  const { isHidden } = useSelectItemData(props.itemKey)
  return <VirtualItemWrapper {...props} isHidden={isHidden} />
}

export const VirtualItemWrapper = ({
  children,
  itemKey,
  selectionManager,
  isHidden,
}: {
  children: ReactNode
  itemKey: Key
  selectionManager: ListState<unknown>['selectionManager']
  isHidden?: boolean
}) => {
  const { subscribe, registerHeight, unregisterHeight } = useContext(VirtualScrollContext)
  const [isVisible, setIsVisible] = useState(false)
  const [height, setHeight] = useState(48)
  const [heightSync] = useState(() => {
    let resizeObserver: ResizeObserver | undefined = undefined
    let currentElement: Element | undefined = undefined
    const checkHeight = () => {
      const height = currentElement?.getBoundingClientRect().height ?? 48
      setHeight(height)
      if (currentElement) {
        registerHeight(currentElement, height)
      }
    }
    return {
      start(element: Element) {
        currentElement = element
        resizeObserver?.disconnect()
        resizeObserver = new ResizeObserver(checkHeight)
        checkHeight()
        resizeObserver.observe(element)
      },
      stop() {
        if (currentElement) {
          unregisterHeight(currentElement)
          currentElement = undefined
        }
        resizeObserver?.disconnect()
        resizeObserver = undefined
      },
    }
  })
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (ref.current) {
      const element = ref.current
      const cancelSubscritpion = subscribe(element, isVisible => {
        if (isVisible) {
          heightSync.start(element)
        }
        setIsVisible(isVisible)
      })
      return () => {
        cancelSubscritpion()
        heightSync.stop()
      }
    }
  }, [subscribe, heightSync])
  const shouldRender =
    isVisible || (selectionManager.isFocused && selectionManager.focusedKey === itemKey)
  return (
    <div
      ref={ref}
      style={{ minHeight: shouldRender ? undefined : height }}
      className={`virtualItem item-${itemKey} ${isHidden ? 'hidden' : 'visible'}`}
    >
      {shouldRender && children}
    </div>
  )
}

const VirtualScrollContext = createContext<{
  registerHeight: (element: Element, height: number) => void
  subscribe: (element: Element, onChange: (visible: boolean) => void) => () => void
  unregisterHeight: (element: Element) => void
}>({
  subscribe: () => doNothing(),
  registerHeight: doNothing(),
  unregisterHeight: doNothing(),
})
export const VirtualScrollProvider = VirtualScrollContext.Provider

export const useVirtualScrollableContainer = (
  containerRef: RefObject<HTMLElement | null>,
  isLoading: boolean, // used to trigger the calculation of visible items if BE search was being done
  search?: string,
  useVirtualScroll = false
) => {
  const [state] = useState(() => {
    let visibleItems: Element[] = []
    const listeners: { callback: (visible: boolean) => void; item: Element }[] = []
    const heightMat = new WeakMap<Element, number>()
    return {
      setVisibleItems(items: readonly Element[]) {
        visibleItems
          .filter(item => !items.includes(item))
          .map(item => listeners.find(l => l.item === item))
          .forEach(listener => listener?.callback(false))
        visibleItems = [...items]
        items
          .map(item => listeners.find(l => l.item === item))
          .forEach(listener => listener?.callback(true))
      },
      subscribe(element: Element, onChange: (visible: boolean) => void) {
        const subscription = { item: element, callback: onChange }
        listeners.push(subscription)
        if (visibleItems.includes(element)) {
          onChange(true)
        }
        return () => {
          const index = listeners.indexOf(subscription)
          listeners.splice(index, 1)
        }
      },
      registerHeight(element: Element, height: number) {
        heightMat.set(element, height)
      },
      unregisterHeight(element: Element) {
        heightMat.delete(element)
      },
      getItemHeight(element: Element) {
        return heightMat.get(element) ?? 48
      },
    }
  })
  useEffect(() => {
    if (containerRef.current && useVirtualScroll && !isLoading) {
      const listElement = containerRef.current

      const calculateVisibleItemsFunnel = funnel(
        () => {
          const options = listElement.querySelectorAll('.virtualItem')
          const filteredOptions = listElement.querySelectorAll('.virtualItem.visible')
          const height = listElement.getBoundingClientRect().height
          const scrollTop = listElement.scrollTop

          const firstItemData = {
            index: 0,
            offset: state.getItemHeight(filteredOptions[0]),
          }
          while (
            firstItemData.offset < scrollTop &&
            firstItemData.index < filteredOptions.length - 1
          ) {
            firstItemData.index++
            firstItemData.offset += state.getItemHeight(filteredOptions[firstItemData.index])
          }
          const lastItemData = {
            index: firstItemData.index,
            offset:
              firstItemData.offset - state.getItemHeight(filteredOptions[firstItemData.index]),
          }
          while (
            lastItemData.offset < scrollTop + height &&
            lastItemData.index < filteredOptions.length - 1
          ) {
            lastItemData.offset += state.getItemHeight(filteredOptions[lastItemData.index])
            lastItemData.index++
          }

          firstItemData.index = Math.max(0, firstItemData.index - 2)
          lastItemData.index = Math.min(filteredOptions.length - 1, lastItemData.index + 2)
          const visibleFilteredItems = Array<number>(lastItemData.index - firstItemData.index + 1)
            .fill(firstItemData.index)
            .map((start, i) => filteredOptions[start + i])
          const visibleItems = Array.from(options).filter(o => {
            if (visibleFilteredItems.includes(o)) {
              const itemIndex = visibleFilteredItems.indexOf(o)
              visibleFilteredItems.splice(itemIndex, 1)

              return true
            }
            // Include all items before the last filtered item
            if (visibleFilteredItems.length) {
              return true
            }
            // If all filtered items are shown but there is still space in the list include the rest to not show scrollbar
            if (lastItemData.offset < height) {
              return true
            }
            return false
          })
          state.setVisibleItems(visibleItems)
        },
        { minQuietPeriodMs: 50, triggerAt: 'both' }
      )
      const calculateVisibleItems = () => calculateVisibleItemsFunnel.call()
      calculateVisibleItems()
      listElement.addEventListener('scroll', calculateVisibleItems, { passive: true })

      const mutationObserver = new MutationObserver(() => {
        calculateVisibleItems()
      })
      mutationObserver.observe(listElement, { childList: true, subtree: true })
      return () => {
        mutationObserver.disconnect()
        listElement.removeEventListener('scroll', calculateVisibleItems)
      }
    }
  }, [containerRef, state, useVirtualScroll, search, isLoading]) // search is needed to retrigger the calculation of visible items only when the search string changes
  return state as Pick<typeof state, 'subscribe' | 'registerHeight' | 'unregisterHeight'>
}
